<template>
  <v-container fluid class="pa-6">
    <!-- Document Header -->
    <v-toolbar class="mb-4" flat>
      <div>
        <h1 class="text-h4 font-weight-light mb-1">Nueva Orden de Compra</h1>
        <div class="text-caption grey--text">
          Gestión de órdenes de compra • {{ formatDateHeader(getCurrentDate()) }}
        </div>
      </div>
      <v-spacer></v-spacer>
      {{ getCurrentDate() }}
    </v-toolbar>

    <!-- Main Document Card -->
    <v-card outlined>
      <!-- Header Section: Store (Left) - Supplier (Right) -->
      <v-container fluid class="pa-0">
        <v-row no-gutters class="equal-height-row">
          <!-- Store Information (Left) -->
          <v-col cols="12" md="6" class="d-flex">
            <v-card
              flat
              tile
              class="pa-4 flex-grow-1"
              style="border-right: 1px solid #e0e0e0"
            >
              <v-card-subtitle class="pa-0 mb-2">
                <v-icon color="primary" class="mr-2">mdi-store</v-icon>
                <span class="text-uppercase font-weight-bold">TIENDA EMISORA</span>
              </v-card-subtitle>
              <v-card-text class="pa-0 flex-grow-1 d-flex flex-column">
                <div class="store-content flex-grow-1">
                  <div class="text-h6 font-weight-bold">
                    {{ currentStore.valor || "Tienda Principal" }}
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    ID: {{ currentStore.store_id || "N/A" }}
                  </div>
                  <div class="text-body-2">{{ currentStore.AddressLine || "" }}</div>
                  <div class="text-body-2">{{ currentStore.CityName || "" }}</div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Supplier Information (Right) -->
          <v-col cols="12" md="6" class="d-flex">
            <v-card flat tile class="pa-4 flex-grow-1">
              <v-card-subtitle
                class="pa-0 mb-2 d-flex justify-space-between align-center"
              >
                <div>
                  <v-icon color="success" class="mr-2">mdi-domain</v-icon>
                  <span class="text-uppercase font-weight-bold">PROVEEDOR</span>
                </div>
              </v-card-subtitle>
              <v-card-text class="pa-0 flex-grow-1 d-flex flex-column">
                <div v-if="supplier" class="supplier-content flex-grow-1">
                  <div class="text-h6 font-weight-bold">
                    {{ supplier.PartyName || supplier.nombre }}
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    NIT: {{ supplier.PartyIdentification || supplier.id_nit }}
                  </div>
                  <div
                    v-if="supplier.AddressLine || supplier.direccion"
                    class="text-body-2"
                  >
                    {{ supplier.AddressLine || supplier.direccion }}
                  </div>
                  <div v-if="supplier.CityName || supplier.ciudad" class="text-body-2">
                    {{ supplier.CityName || supplier.ciudad }}
                  </div>
                  <div v-if="supplier.PartyEmail || supplier.email" class="text-body-2">
                    Email: {{ supplier.PartyEmail || supplier.email }}
                  </div>
                  <div v-if="supplier.PartyPhone || supplier.celular" class="text-body-2">
                    Tel: {{ supplier.PartyPhone || supplier.celular }}
                  </div>
                  <div class="mt-auto pt-2">
                    <v-btn text small color="primary" @click="supplier_dialog = true">
                      <v-icon left small>mdi-pencil</v-icon>
                      Cambiar Proveedor
                    </v-btn>
                  </div>
                </div>
                <div
                  v-else
                  class="supplier-content flex-grow-1 d-flex align-center justify-center"
                >
                  <v-alert type="info" text class="ma-0 text-center">
                    <div>
                      <v-icon size="32">mdi-domain-plus</v-icon>
                      <div class="mt-2">Seleccionar proveedor</div>
                      <p class="text-caption grey--text mt-2">
                        Debe seleccionar un proveedor para crear la orden
                      </p>
                      <v-btn color="primary" @click="supplier_dialog = true" class="mt-3">
                        <v-icon left>mdi-account-plus</v-icon>
                        Seleccionar Proveedor
                      </v-btn>
                    </div>
                  </v-alert>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-divider></v-divider>

      <!-- KPI Dashboard -->
      <v-container fluid class="pa-4" v-if="cart.length > 0">
        <v-row class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="text-center pa-3">
              <div class="stat-value primary--text">{{ cart.length }}</div>
              <div class="stat-label">
                <v-icon small color="primary" class="mr-1">mdi-package-variant</v-icon>
                Referencias únicas
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="text-center pa-3">
              <div class="stat-value success--text">{{ formatNumber(totals.qty) }}</div>
              <div class="stat-label">
                <v-icon small color="success" class="mr-1">mdi-cube-outline</v-icon>
                Unidades Total
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="text-center pa-3">
              <div class="stat-value warning--text">
                {{ formatCurrency(totals.total) }}
              </div>
              <div class="stat-label">
                <v-icon small color="warning" class="mr-1">mdi-currency-usd</v-icon>
                Valor Total
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="text-center pa-3">
              <div class="stat-value info--text">{{ averageItemCost }}</div>
              <div class="stat-label">
                <v-icon small color="info" class="mr-1">mdi-calculator</v-icon>
                Costo Promedio
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Items Section -->
      <v-container fluid class="pa-4">
        <v-row>
          <v-col cols="12">
            <v-card-subtitle class="pa-0 mb-3 d-flex justify-space-between align-center">
              <div>
                <v-icon color="orange" class="mr-2">mdi-cart-outline</v-icon>
                <span class="text-uppercase font-weight-bold">PRODUCTOS EN LA ORDEN</span>
              </div>
              <v-btn color="orange" @click="product_dialog = true" :disabled="!supplier">
                <v-icon left>mdi-plus</v-icon>
                Agregar Productos
              </v-btn>
            </v-card-subtitle>

            <v-data-table
              v-if="cart.length > 0"
              :headers="cartHeaders"
              :items="cart"
              :items-per-page="-1"
              class="minimal-table"
              hide-default-footer
            >
              <!-- Product Info -->
              <template v-slot:item.product="{ item }">
                <div class="d-flex align-center">
                  <v-icon small color="orange" class="mr-2">mdi-tag</v-icon>
                  <div class="product-info">
                    <div class="product-name font-weight-medium">{{ item.nombre }}</div>
                    <div class="product-details">
                      <span class="mr-3">
                        <v-icon x-small class="mr-1">mdi-tag</v-icon>
                        {{ item.StoreEan }}
                      </span>
                      <span
                        >{{ item.brand }} - Ref: {{ item.refe }} -
                        {{ item.colorName }}</span
                      >
                    </div>
                  </div>
                </div>
              </template>

              <!-- Category -->
              <template v-slot:item.categoria="{ item }">
                <div>
                  <div>{{ item.category }}</div>
                  <div class="text-caption grey--text">{{ item.groupName }}</div>
                </div>
              </template>

              <!-- Size -->
              <template v-slot:item.talla="{ item }">
                <v-chip small outlined color="primary">
                  {{ item.talla || "N/A" }}
                </v-chip>
              </template>

              <!-- Quantity -->
              <template v-slot:item.cantidad="{ item }">
                <div class="quantity-control">
                  <v-btn icon x-small @click="decreaseQuantity(item)">
                    <v-icon x-small>mdi-minus</v-icon>
                  </v-btn>
                  <v-text-field
                    v-model="item.cantidad"
                    type="number"
                    min="1"
                    dense
                    hide-details
                    class="quantity-input"
                    @change="updateTotal"
                  ></v-text-field>
                  <v-btn icon x-small @click="increaseQuantity(item)">
                    <v-icon x-small>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </template>

              <!-- Cost -->
              <template v-slot:item.subtotal="{ item }">
                <div class="text-right">
                  <div class="font-weight-bold">
                    {{ formatCurrency(item.costo * item.cantidad) }}
                  </div>
                  <div class="text-caption grey--text">
                    {{ formatCurrency(item.costo) }} c/u
                  </div>
                </div>
              </template>

              <!-- Actions -->
              <template v-slot:item.actions="{ item }">
                <v-btn icon small color="error" @click="delete_item(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>

              <template v-slot:body.append v-if="cart.length > 0">
                <tr class="grey lighten-4">
                  <td colspan="4" class="text-right font-weight-bold text-uppercase">
                    Total de la Orden
                  </td>
                  <td class="text-right">
                    <span class="text-h6 font-weight-bold primary--text">
                      {{ formatCurrency(totals.total) }}
                    </span>
                  </td>
                  <td>
                    <v-btn text color="error" @click="clearCart" small>
                      <v-icon left small>mdi-delete-sweep</v-icon>
                      Vaciar
                    </v-btn>
                  </td>
                </tr>
              </template>

              <template v-slot:no-data>
                <v-container class="text-center py-8">
                  <v-icon size="64" color="grey lighten-2">mdi-cart-outline</v-icon>
                  <div class="text-h6 mt-4 grey--text">La orden está vacía</div>
                  <div class="text-caption grey--text mt-2">
                    Agregue productos para crear una orden de compra
                  </div>
                </v-container>
              </template>
            </v-data-table>

            <!-- Empty State -->
            <v-alert v-else type="info" text>
              <div class="text-center">
                <v-icon size="64" color="grey lighten-2">mdi-cart-outline</v-icon>
                <div class="text-h6 mt-4 grey--text">La orden está vacía</div>
                <div class="text-caption grey--text mt-2">
                  Agregue productos para crear una orden de compra
                </div>
                <v-btn
                  color="primary"
                  large
                  @click="product_dialog = true"
                  :disabled="!supplier"
                  class="mt-4"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Agregar Productos
                </v-btn>
              </div>
            </v-alert>
          </v-col>
        </v-row>
      </v-container>

      <v-divider v-if="cart.length > 0"></v-divider>

      <!-- Bottom Section: Motivo (Left) - Resumen (Right) -->
      <v-container fluid class="pa-0" v-if="cart.length > 0">
        <v-row no-gutters style="min-height: 200px">
          <!-- Motivo Selection (Left) -->
          <v-col
            cols="12"
            md="7"
            class="pa-4 d-flex flex-column"
            style="border-right: 1px solid #e0e0e0"
          >
            <div class="mb-4">
              <v-icon color="success" class="mr-2">mdi-tag-text</v-icon>
              <span class="text-uppercase font-weight-bold">Condiciones</span>
            </div>

            <div class="flex-grow-1">
              <v-form
                v-model="validatemotive"
                ref="motiveform"
                @submit.prevent="save_receipt"
              >
                <v-select
                  v-model="motive"
                  :items="FP"
                  item-value="codigo"
                  item-text="valor"
                  return-object
                  outlined
                  label="Seleccione el motivo"
                  :rules="f_required"
                  prepend-inner-icon="mdi-tag-text"
                  hide-details
                  dense
                />
              </v-form>
            </div>
          </v-col>

          <!-- Order Summary (Right) -->
          <v-col cols="12" md="5" class="pa-4 d-flex flex-column">
            <div class="mb-4">
              <v-icon color="primary" class="mr-2">mdi-calculator</v-icon>
              <span class="text-uppercase font-weight-bold">RESUMEN DE LA ORDEN</span>
            </div>

            <div class="flex-grow-1 d-flex flex-column">
              <v-simple-table>
                <tbody>
                  <tr>
                    <td class="text-left">
                      <v-icon small class="mr-2" color="primary"
                        >mdi-package-variant</v-icon
                      >
                      Total Productos
                    </td>
                    <td class="text-right font-weight-bold">
                      {{ cart.length }}
                    </td>
                  </tr>
                  <tr>
                    <td class="text-left">
                      <v-icon small class="mr-2" color="success">mdi-cube-outline</v-icon>
                      Total Unidades
                    </td>
                    <td class="text-right font-weight-bold success--text">
                      {{ formatNumber(totals.qty) }}
                    </td>
                  </tr>
                  <tr class="grey lighten-3">
                    <td class="text-left">
                      <v-icon small class="mr-2" color="warning">mdi-currency-usd</v-icon>
                      <strong>Valor Total</strong>
                    </td>
                    <td class="text-right font-weight-bold warning--text">
                      {{ formatCurrency(totals.total) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <v-alert
                :type="isOrderReady() ? 'success' : 'warning'"
                text
                dense
                class="mt-3 mb-3"
              >
                <div class="d-flex align-center">
                  <v-icon class="mr-2">{{
                    isOrderReady() ? "mdi-check-circle" : "mdi-clock-alert"
                  }}</v-icon>
                  {{
                    isOrderReady()
                      ? "Orden lista para crear"
                      : "Complete los datos requeridos"
                  }}
                </div>
              </v-alert>

              <v-spacer></v-spacer>

              <v-btn
                color="primary"
                large
                block
                :disabled="!isOrderReady()"
                :loading="loading_status"
                @click="save_receipt"
                class="mt-auto"
              >
                <v-icon left>mdi-check</v-icon>
                Crear Orden
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Status Chip (Top Right) -->
    <v-chip v-if="isOrderReady()" color="success" outlined class="status-chip">
      <v-icon left small>mdi-check-circle</v-icon>
      Lista para crear
    </v-chip>
    <v-chip
      v-else-if="cart.length > 0 || supplier"
      color="warning"
      outlined
      class="status-chip"
    >
      <v-icon left small>mdi-progress-clock</v-icon>
      En progreso
    </v-chip>

    <!-- Supplier Selection Dialog -->
    <v-dialog
      v-model="supplier_dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="900"
      scrollable
    >
      <v-card class="elevation-0">
        <v-toolbar flat class="minimal-toolbar">
          <h3 class="dialog-title">Seleccionar Proveedor</h3>
          <v-spacer></v-spacer>
          <v-btn icon @click="supplier_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <!-- Formulario de búsqueda de proveedores -->
          <v-form @submit.prevent="search_suppliers">
            <v-row dense>
              <v-col cols="12" md="9">
                <v-text-field
                  v-model="supplier_search"
                  label="Buscar proveedores"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  @keypress.enter="search_suppliers"
                  placeholder="Ingrese nombre, NIT o código del proveedor..."
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-btn block color="primary" type="submit" :loading="loading_suppliers">
                  <v-icon left>mdi-magnify</v-icon>
                  {{
                    supplier_search && supplier_search.trim() ? "Buscar" : "Mostrar Todos"
                  }}
                </v-btn>
              </v-col>
            </v-row>
          </v-form>

          <!-- Tabla de proveedores -->
          <v-data-table
            v-if="suppliers_list.length > 0"
            :headers="supplier_headers"
            :items="suppliers_list"
            :items-per-page="10"
            class="minimal-table elevation-1 mt-4"
            :loading="loading_suppliers"
            loading-text="Buscando proveedores..."
          >
            <!-- Supplier Info -->
            <template v-slot:item.info="{ item }">
              <div class="supplier-info">
                <div class="font-weight-medium">{{ item.nombre }}</div>
                <div class="text-caption grey--text">NIT: {{ item.id_nit || "N/A" }}</div>
                <div class="text-caption grey--text" v-if="item.codigo">
                  Código: {{ item.codigo }}
                </div>
              </div>
            </template>

            <!-- Contact Info -->
            <template v-slot:item.contact="{ item }">
              <div>
                <div v-if="item.email" class="text-caption">
                  <v-icon x-small>mdi-email</v-icon>
                  {{ item.email }}
                </div>
                <div v-if="item.telefono" class="text-caption">
                  <v-icon x-small>mdi-phone</v-icon>
                  {{ item.telefono }}
                </div>
              </div>
            </template>

            <!-- Address -->
            <template v-slot:item.address="{ item }">
              <div class="text-caption">
                <div v-if="item.direccion">{{ item.direccion }}</div>
                <div v-if="item.Ciudad">{{ item.Ciudad }}</div>
              </div>
            </template>

            <!-- Actions -->
            <template v-slot:item.actions="{ item }">
              <v-btn color="primary" small @click="select_supplier(item)">
                <v-icon left small>mdi-check</v-icon>
                Seleccionar
              </v-btn>
            </template>

            <!-- No data state -->
            <template v-slot:no-data>
              <div class="text-center py-8">
                <v-icon size="64" color="grey lighten-2">
                  {{ loading_suppliers ? "mdi-loading mdi-spin" : "mdi-account-search" }}
                </v-icon>
                <h3 class="text-h6 mt-4 grey--text">
                  {{
                    loading_suppliers
                      ? "Buscando proveedores..."
                      : "No se encontraron proveedores"
                  }}
                </h3>
                <p class="grey--text" v-if="!loading_suppliers">
                  {{
                    has_searched_suppliers
                      ? "Intente con otros criterios de búsqueda"
                      : "Use el formulario de búsqueda para encontrar proveedores"
                  }}
                </p>
              </div>
            </template>
          </v-data-table>

          <!-- Mensaje cuando no hay datos -->
          <v-alert
            v-if="
              !loading_suppliers && suppliers_list.length === 0 && !has_searched_suppliers
            "
            type="info"
            outlined
            class="mt-4"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="font-weight-medium">Busque proveedores</div>
                <div class="text-caption">
                  Ingrese el nombre, NIT o código del proveedor para comenzar la búsqueda
                </div>
              </div>
              <v-btn
                color="primary"
                @click="search_suppliers"
                :loading="loading_suppliers"
              >
                <v-icon left>mdi-eye</v-icon>
                Ver Todos
              </v-btn>
            </div>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Supplier Change Confirmation Dialog -->
    <v-dialog v-model="confirm_change_dialog" max-width="500" persistent>
      <v-card class="elevation-8">
        <v-card-title class="warning white--text d-flex align-center">
          <v-icon color="white" class="mr-3">mdi-alert-circle</v-icon>
          <span>Cambiar Proveedor</span>
        </v-card-title>

        <v-card-text class="pa-6">
          <div class="mb-4">
            <p class="text-body-1 mb-3">¿Está seguro de cambiar el proveedor actual?</p>

            <div class="change-summary">
              <div class="d-flex align-center mb-2">
                <v-icon color="error" class="mr-2">mdi-arrow-right</v-icon>
                <span class="font-weight-medium">De:</span>
                <span class="ml-2 text-decoration-line-through">{{
                  supplier && supplier.PartyName ? supplier.PartyName : "Sin proveedor"
                }}</span>
              </div>
              <div class="d-flex align-center mb-4">
                <v-icon color="success" class="mr-2">mdi-arrow-right</v-icon>
                <span class="font-weight-medium">A:</span>
                <span class="ml-2 font-weight-bold success--text">{{
                  pending_supplier && pending_supplier.nombre
                    ? pending_supplier.nombre
                    : ""
                }}</span>
              </div>
            </div>

            <v-alert
              type="warning"
              outlined
              class="mb-4"
              v-if="change_impact.cart_items > 0 || change_impact.search_results > 0"
            >
              <div class="font-weight-bold mb-2 d-flex align-center">
                <v-icon small color="warning" class="mr-2">mdi-alert-circle</v-icon>
                Esta acción limpiará:
              </div>

              <div v-if="change_impact.cart_items > 0" class="d-flex align-center mb-1">
                <v-icon small color="warning" class="mr-2">mdi-cart</v-icon>
                <span>{{ change_impact.cart_items }} productos en el carrito</span>
              </div>

              <div
                v-if="change_impact.search_results > 0"
                class="d-flex align-center mb-1"
              >
                <v-icon small color="warning" class="mr-2">mdi-magnify</v-icon>
                <span>{{ change_impact.search_results }} resultados de búsqueda</span>
              </div>

              <div v-if="change_impact.has_motive" class="d-flex align-center mb-1">
                <v-icon small color="warning" class="mr-2">mdi-tag-text</v-icon>
                <span>Motivo y condiciones seleccionadas</span>
              </div>

              <div class="d-flex align-center mt-2">
                <v-icon small color="error" class="mr-2">mdi-information</v-icon>
                <span class="font-weight-medium">Esta acción no se puede deshacer</span>
              </div>
            </v-alert>

            <v-alert type="info" text v-else>
              <div class="d-flex align-center">
                <v-icon color="info" class="mr-2">mdi-information</v-icon>
                <span
                  >No hay datos que limpiar. El proveedor se cambiará sin problemas.</span
                >
              </div>
            </v-alert>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn text @click="cancelSupplierChange" class="mr-2">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            color="warning"
            @click="confirmSupplierChange"
            :loading="loading_suppliers"
          >
            <v-icon left>mdi-check</v-icon>
            Cambiar Proveedor
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Product Search Dialog -->
    <v-dialog
      v-model="product_dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="1200"
      scrollable
    >
      <v-card class="elevation-0">
        <v-toolbar flat class="minimal-toolbar">
          <h3 class="dialog-title">Buscar Productos</h3>
          <v-spacer></v-spacer>
          <v-btn icon @click="product_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <!-- Formulario de búsqueda mejorado (copiado de InvoiceE) -->
          <v-form @submit.prevent="search_inventory">
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.field"
                  :items="searchFields"
                  label="Criterio de búsqueda"
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-filter"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="filters.value"
                  label="Buscar productos"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  @keypress.enter="search_inventory"
                  autofocus
                  placeholder="Ingrese el término de búsqueda..."
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-btn
                  block
                  color="primary"
                  type="submit"
                  :loading="loading_products"
                  :disabled="!filters.value || filters.value.trim() === ''"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Buscar
                </v-btn>
              </v-col>
            </v-row>
          </v-form>

          <!-- Quick Stats (mejorados) -->
          <v-row v-if="groupedData.length > 0" class="mb-4 mt-4">
            <v-col cols="12" sm="3">
              <v-card outlined class="text-center pa-3">
                <div class="stat-value primary--text">{{ groupedData.length }}</div>
                <div class="stat-label">Productos encontrados</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="3">
              <v-card outlined class="text-center pa-3">
                <div class="stat-value success--text">{{ recommendedCount }}</div>
                <div class="stat-label">Recomendados</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="3">
              <v-card outlined class="text-center pa-3">
                <div class="stat-value warning--text">{{ lowStockCount }}</div>
                <div class="stat-label">Stock bajo</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="3">
              <v-card outlined class="text-center pa-3">
                <div class="stat-value info--text">
                  {{ formatCurrency(totalSearchValue) }}
                </div>
                <div class="stat-label">Valor total</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Mensaje cuando no hay datos -->
          <v-alert
            v-if="
              !loading_products &&
              groupedData.length === 0 &&
              (filters.value || hasSearched)
            "
            type="info"
            outlined
            class="mb-4"
          >
            <div class="d-flex align-center">
              <v-icon left>mdi-information</v-icon>
              <div>
                <div class="font-weight-medium">No se encontraron productos</div>
                <div class="text-caption">
                  Intente con otros criterios:
                  {{ searchFields.map((f) => f.toLowerCase()).join(", ") }}
                </div>
              </div>
            </div>
          </v-alert>

          <!-- Products Table -->
          <v-data-table
            :headers="enhancedProductHeaders"
            :items="groupedData"
            :items-per-page="15"
            class="minimal-table elevation-1"
            :loading="loading_products"
            loading-text="Buscando productos..."
            search=""
            :sort-by="['cantidad']"
            :sort-desc="[true]"
          >
            <!-- Product Info -->
            <template v-slot:item.producto="{ item }">
              <div class="product-search-info">
                <div class="font-weight-medium text-truncate" style="max-width: 200px">
                  {{ item.nombre }}
                </div>
                <div class="text-caption grey--text">
                  <v-chip x-small outlined class="mr-1">{{ item.brand }}</v-chip>
                  <span>Ref: {{ item.refe }}</span>
                  <span v-if="item.color"> • {{ item.colorName }}</span>
                </div>
                <div v-if="item.ean" class="text-caption grey--text mt-1">
                  <v-icon x-small>mdi-barcode</v-icon>
                  {{ item.ean }}
                </div>
              </div>
            </template>

            <!-- Category with hierarchy -->
            <template v-slot:item.categoria="{ item }">
              <div>
                <v-chip x-small color="primary" outlined class="mb-1">
                  {{ item.category }}
                </v-chip>
                <div class="text-caption grey--text">{{ item.grupo }}</div>
              </div>
            </template>

            <!-- Size -->
            <template v-slot:item.talla="{ item }">
              <v-chip
                x-small
                :color="item.talla ? 'success' : 'grey'"
                :outlined="!item.talla"
                text-color="white"
              >
                {{ item.talla || "Universal" }}
              </v-chip>
            </template>

            <!-- Enhanced Stock Status -->
            <template v-slot:item.stock="{ item }">
              <div class="stock-info text-center">
                <div :class="getStockClass(item.qty)" class="font-weight-medium">
                  <v-icon x-small class="mr-1">mdi-package-variant</v-icon>
                  {{ item.qty || 0 }}
                </div>
                <div class="text-caption grey--text">
                  <v-icon x-small class="mr-1">mdi-trending-up</v-icon>
                  {{ item.sales_qty || 0 }} vendidos
                </div>
                <!-- Indicador visual de rotación -->
                <v-progress-linear
                  v-if="getStockRotation(item) > 0"
                  :value="Math.min(getStockRotation(item) * 100, 100)"
                  height="4"
                  :color="getRotationColor(item)"
                  class="mt-1"
                  rounded
                ></v-progress-linear>
              </div>
            </template>

            <!-- Enhanced Price Info -->
            <template v-slot:item.precio="{ item }">
              <div class="text-right">
                <div class="font-weight-medium">C: ${{ formatCurrency(item.costo) }}</div>
                <div class="text-caption grey--text">
                  V: {{ formatCurrency(item.precio) }}
                </div>
                <div class="text-caption">
                  <span :class="getMarginClass(item.margen)">
                    <v-icon x-small>mdi-trending-up</v-icon>
                    {{ (item.margen * 100).toFixed(0) }}%
                  </span>
                </div>
              </div>
            </template>

            <!-- Enhanced Recommendation -->
            <template v-slot:item.recomendacion="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <div class="text-center">
                    <v-chip
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      :color="getRecommendation(item).color"
                      text-color="white"
                      class="mb-1"
                    >
                      <v-icon left x-small>{{ getRecommendation(item).icon }}</v-icon>
                      {{ getRecommendation(item).text }}
                    </v-chip>
                    <!-- Sugerencia de cantidad automática -->
                    <div
                      v-if="getRecommendation(item).suggestedQty > 0"
                      class="text-caption success--text"
                    >
                      Sugerir: {{ getRecommendation(item).suggestedQty }}
                    </div>
                  </div>
                </template>
                <div>
                  <strong>{{ getRecommendation(item).reason }}</strong>
                  <div v-if="getRecommendation(item).suggestedQty > 0" class="mt-1">
                    Cantidad sugerida: {{ getRecommendation(item).suggestedQty }} unidades
                  </div>
                </div>
              </v-tooltip>
            </template>

            <!-- Enhanced Quantity Input -->
            <template v-slot:item.cantidad="{ item }">
              <div class="quantity-container">
                <v-text-field
                  v-model.number="item.cantidad"
                  type="number"
                  min="0"
                  :max="999"
                  dense
                  outlined
                  hide-details
                  class="quantity-search-input"
                  @focus="$event.target.select()"
                >
                  <template v-slot:append>
                    <v-btn
                      x-small
                      text
                      color="primary"
                      @click="applySuggestedQuantity(item)"
                      v-if="getRecommendation(item).suggestedQty > 0"
                      class="ml-1"
                    >
                      Auto
                    </v-btn>
                  </template>
                </v-text-field>
              </div>
            </template>

            <!-- Enhanced Add Action -->
            <template v-slot:item.actions="{ item }">
              <div class="text-center">
                <v-btn
                  :color="item.cantidad > 0 ? 'success' : 'primary'"
                  :variant="item.cantidad > 0 ? 'elevated' : 'outlined'"
                  fab
                  x-small
                  @click="add_to_order(item)"
                  :disabled="!item.cantidad || item.cantidad <= 0"
                >
                  <v-icon>{{
                    item.cantidad > 0 ? "mdi-cart-plus" : "mdi-cart-outline"
                  }}</v-icon>
                </v-btn>
              </div>
            </template>

            <!-- No data state -->
            <template v-slot:no-data>
              <div class="text-center py-12">
                <v-icon size="64" color="grey lighten-2">
                  {{ loading_products ? "mdi-loading mdi-spin" : "mdi-database-search" }}
                </v-icon>
                <h3 class="text-h6 mt-4 grey--text">
                  {{
                    loading_products
                      ? "Buscando productos..."
                      : "No se encontraron productos"
                  }}
                </h3>
                <p class="grey--text" v-if="!loading_products">
                  {{
                    hasSearched
                      ? "Intente con otros criterios de búsqueda"
                      : "Use el formulario de búsqueda para encontrar productos"
                  }}
                </p>
              </div>
            </template>
          </v-data-table>

          <!-- Actions Footer -->
          <v-card-actions class="pa-0 mt-4" v-if="groupedData.length > 0">
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-btn
                  text
                  color="primary"
                  @click="selectAllRecommended"
                  :disabled="recommendedCount === 0"
                >
                  <v-icon left>mdi-check-all</v-icon>
                  Seleccionar Recomendados ({{ recommendedCount }})
                </v-btn>
              </v-col>
              <v-col cols="12" sm="6" class="text-right">
                <v-btn text color="grey" @click="clearAllQuantities">
                  <v-icon left>mdi-eraser</v-icon>
                  Limpiar Cantidades
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-dialog>

    <docViewer
      v-if="orderDocument"
      :item="orderDocument"
      :dialog="showOrderDocument"
      @close="showOrderDocument = false"
      @new-order="handleNewOrder"
      @view-list="handleViewList"
    />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, formatNumber, formatCurrency } from "@/services/webserver.js";
import docViewer from "@/components/doc_viewer_docs.vue";

export default {
  components: {
    docViewer,
  },

  watch: {
    // Watch for supplier changes to provide additional warnings
    supplier: {
      handler(newSupplier, oldSupplier) {
        // Only show warning if we actually changed suppliers (not on initial load)
        if (oldSupplier && newSupplier && oldSupplier.code !== newSupplier.code) {
          console.log(
            `Supplier changed from ${oldSupplier.PartyName} to ${newSupplier.PartyName}`
          );

          // Additional safety check - ensure product data is clean
          setTimeout(() => {
            if (this.groupedData.length > 0) {
              console.warn(
                "Warning: Product search data still present after supplier change"
              );
              this.groupedData = [];
              this.hasSearched = false;
            }
          }, 100);
        }
      },
      deep: true,
    },
  },
  data() {
    return {
      formatNumber,
      showOrderDocument: false,
      orderDocument: null,
      // UI State
      snackbar: false,
      snackbarText: "",
      snackbarColor: "info",
      loading_status: false,
      loading_products: false,
      product_dialog: false,
      showPrint: false,

      // Supplier Selection
      supplier_dialog: false,
      loading_suppliers: false,
      supplier_search: "",
      suppliers_list: [],
      all_suppliers: [],
      has_searched_suppliers: false,

      // Supplier Change Confirmation Dialog
      confirm_change_dialog: false,
      pending_supplier: null,
      change_impact: {
        cart_items: 0,
        search_results: 0,
        has_motive: false,
      },

      // Order Data
      cart: [],
      supplier: null,
      currentStore: {},
      totals: { qty: 0, total: 0 },

      // Search Data
      productName: "",
      selectedCategoria: null,
      color: null,
      groupedData: [],
      categorias: [],
      colores: [],

      // Form Data
      validatemotive: false,
      motive: null,
      FP: [],
      f_required: [(v) => !!v || "Campo requerido"],

      // Print Data
      dataURL: "",

      // Table Headers
      cartHeaders: [
        { text: "Producto", value: "product", sortable: false, width: "35%" },
        { text: "Categoría", value: "categoria", width: "20%" },
        { text: "Talla", value: "talla", align: "center", width: "10%" },
        { text: "Cantidad", value: "cantidad", align: "center", width: "15%" },
        { text: "Subtotal", value: "subtotal", align: "end", width: "15%" },
        { text: "", value: "actions", align: "center", sortable: false, width: "5%" },
      ],

      productHeaders: [
        { text: "Producto", value: "producto", width: "30%" },
        { text: "Categoría", value: "categoria", width: "15%" },
        { text: "Talla", value: "talla", align: "center", width: "8%" },
        { text: "Stock", value: "stock", align: "center", width: "12%" },
        { text: "Precio", value: "precio", align: "end", width: "12%" },
        { text: "Recom.", value: "recomendacion", align: "center", width: "10%" },
        { text: "Cant.", value: "cantidad", align: "center", width: "8%" },
        { text: "", value: "actions", align: "center", sortable: false, width: "5%" },
      ],
      filters: {
        field: "NOMBRE",
        value: "",
      },
      searchFields: ["NOMBRE", "CATEGORIA", "GRUPO", "MARCA", "REFE", "COLOR", "TALLA"],
      hasSearched: false,

      // Headers mejorados
      enhancedProductHeaders: [
        { text: "Producto", value: "producto", width: "30%", sortable: false },
        { text: "Categoría", value: "categoria", width: "15%" },
        { text: "Talla", value: "talla", align: "center", width: "8%" },
        { text: "Stock", value: "stock", align: "center", width: "12%" },
        { text: "Precios", value: "precio", align: "center", width: "12%" },
        { text: "Recomendación", value: "recomendacion", align: "center", width: "10%" },
        { text: "Cantidad", value: "cantidad", align: "center", width: "8%" },
        { text: "", value: "actions", align: "center", sortable: false, width: "5%" },
      ],

      // Supplier table headers
      supplier_headers: [
        { text: "Proveedor", value: "info", width: "35%", sortable: false },
        { text: "Contacto", value: "contact", width: "25%", sortable: false },
        { text: "Dirección", value: "address", width: "25%", sortable: false },
        { text: "", value: "actions", align: "center", sortable: false, width: "15%" },
      ],
    };
  },

  computed: {
    totalSearchValue() {
      return this.groupedData.reduce((total, item) => {
        return total + (parseFloat(item.costo) || 0) * (parseInt(item.qty) || 0);
      }, 0);
    },
    averageItemCost() {
      if (this.totals.qty === 0) return 0;
      return this.formatCurrency(Math.round(this.totals.total / this.totals.qty));
    },

    recommendedCount() {
      return this.groupedData.filter((item) => {
        const rec = this.getRecommendation(item);
        return rec.color === "success";
      }).length;
    },

    lowStockCount() {
      return this.groupedData.filter((item) => parseInt(item.qty) < 5).length;
    },
  },

  mounted() {
    // Initialize store data
    this.initializeStore();

    // Load supplier if passed as parameter
    if (this.$route.params && this.$route.params.supplier) {
      const suppy = this.$route.params.supplier;
      console.log(suppy);
      this.supplier = {
        code: suppy.code,
        PartyName: suppy.nombre,
        PartyIdentification: suppy.id_nit,
        AddressLine: suppy.direccion,
        CityName: suppy.ciudad,
        PartyEmail: suppy.email,
        PartyPhone: suppy.celular,
      };

      //this.supplier = this.$route.params.supplier;
    }

    console.log(this.supplier);

    // Load order motives
    this.FP = window.settings["OP"] || [];

    // Load saved cart
    this.loadCart();

    // Load suppliers list for dialog
    this.loadSuppliersForDialog();
  },

  methods: {
    formatCurrency,

    // Nuevos métodos para el layout
    formatDateHeader(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    getCurrentDate() {
      return new Date().toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    isOrderReady() {
      return this.cart.length > 0 && this.supplier && this.motive && this.validatemotive;
    },

    // Initialization
    initializeStore() {
      // Set current store from window.store
      this.currentStore = {
        store_id: window.store?.store_id || "",
        valor: window.store?.store_name || "Tienda Principal",
        CityName: window.store?.store_city || "Ciudad",
        AddressLine: window.store?.store_address || "",
        PartyIdentification: window.store?.store_id || "",
        PartyName: window.store?.store_name || "",
      };
    },

    loadCart() {
      if (localStorage.cart) {
        try {
          this.cart = JSON.parse(Buffer.from(localStorage.cart, "base64"));
          this.updateTotal();
        } catch (e) {
          console.error("Error loading cart", e);
          localStorage.removeItem("cart");
        }
      }
    },

    // UI Methods
    showNotification(message, color = "info") {
      this.snackbarText = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    search_inventory(e) {
      if (e) e.preventDefault();

      if (!this.filters.value || this.filters.value.trim() === "") {
        this.showNotification("Ingrese un término de búsqueda", "warning");
        return;
      }

      this.hasSearched = true;
      this.loading_products = true;

      const qry = {
        store: window.store.store_id,
        dbSearch: "inventory",
        filters: [
          {
            field: "id_pos",
            operator: "=",
            value: window.store.store_id.substring(0, 6),
          },
          {
            field: "id_proveedor",
            operator: "=",
            value: this.supplier.code,
          },
          // {
          //   field: "cantidad",
          //   operator: ">",
          //   value: 0,
          // },
          {
            field: this.filters.field,
            operator: " LIKE ",
            value: this.filters.value,
          },
        ],
      };

      console.log(qry);

      webserver("get_catalog", qry, (data) => {
        console.log(data);
        this.loading_products = false;

        if (data && data.length > 0) {
          // Procesar datos para mejorar la visualización
          this.groupedData = data.map((item) => {
            const cost = parseFloat(item.costo) || 0;
            const price = parseFloat(item.precio) || 0;

            // Calcular margen
            item.margen = cost > 0 ? (price - cost) / price : 0;

            // Normalizar propiedades para consistencia
            item.qty = parseInt(item.cantidad) || 0;
            item.sales_qty = parseInt(item.sales_qty) || 0;

            // Inicializar cantidad para orden
            item.cantidad = 0; // Reset para la cantidad a ordenar

            return item;
          });

          this.showNotification(`${data.length} productos encontrados`, "success");
        } else {
          this.groupedData = [];
          this.showNotification("No se encontraron productos", "info");
        }
      });
    },

    // Cart Management
    add_to_order(item) {
      const quantity = parseInt(item.cantidad);
      if (!quantity || quantity <= 0) {
        this.showNotification("Ingrese una cantidad válida", "warning");
        return;
      }

      const existingIndex = this.cart.findIndex((el) => el.ean === item.ean);

      if (existingIndex !== -1) {
        this.cart[existingIndex].cantidad += quantity;
        this.showNotification("Cantidad actualizada", "success");
      } else {
        this.cart.push({ ...item, cantidad: quantity });
        this.showNotification("Producto agregado", "success");
      }

      // Reset quantity in search
      item.cantidad = 0;

      this.updateTotal();
      this.saveCart();
    },

    delete_item(item) {
      const index = this.cart.findIndex((el) => el.StoreEan === item.StoreEan);
      if (index !== -1) {
        this.cart.splice(index, 1);
        this.showNotification("Producto eliminado", "info");
        this.updateTotal();
        this.saveCart();
      }
    },

    clearCart() {
      if (confirm("¿Está seguro de vaciar la orden?")) {
        this.cart = [];
        this.updateTotal();
        this.saveCart();

        // Also clear search results to avoid confusion
        this.groupedData = [];
        this.hasSearched = false;
        this.filters = {
          field: "NOMBRE",
          value: "",
        };

        this.showNotification("Orden vaciada y búsqueda limpiada", "info");
      }
    },

    increaseQuantity(item) {
      item.cantidad = parseInt(item.cantidad) + 1;
      this.updateTotal();
    },

    decreaseQuantity(item) {
      if (item.cantidad > 1) {
        item.cantidad = parseInt(item.cantidad) - 1;
        this.updateTotal();
      }
    },

    updateTotal() {
      this.totals.qty = 0;
      this.totals.total = 0;

      this.cart.forEach((item) => {
        const qty = parseInt(item.cantidad) || 0;
        const cost = parseFloat(item.costo) || 0;
        this.totals.qty += qty;
        this.totals.total += qty * cost;
      });

      this.saveCart();
    },

    saveCart() {
      try {
        localStorage.cart = Buffer.from(JSON.stringify(this.cart)).toString("base64");
      } catch (e) {
        console.error("Error saving cart", e);
      }
    },

    // Order Processing
    save_receipt(e) {
      e.preventDefault();

      if (!this.$refs.motiveform.validate()) {
        this.showNotification("Complete todos los campos", "error");
        return;
      }

      if (!this.cart.length) {
        this.showNotification("Agregue productos a la orden", "error");
        return;
      }

      this.loading_status = true;

      const qry = {
        doc: {
          doc_type: "OP",
          doc_value: this.totals.total,
          store: this.currentStore.store_id,
          userId: window.userId || window.profile.user_email,
        },
        items: this.cart,
        paidTo: this.supplier,
        seller: this.currentStore,
        store: this.currentStore.store_id,
        payments: [
          {
            payment_code: this.motive.codigo,
            payment_concept: this.motive.valor,
            payment_reference: "",
            payment_value: this.totals.total,
          },
        ],
      };

      webserver("put_stock", qry, (data) => {
        this.loading_status = false;
        if (data && data.success !== false) {
          this.clearCartAfterSave();
          this.showNotification("Orden creada exitosamente", "success");
          this.orderDocument = data;

          this.showOrderDocument = true;
        } else {
          this.showNotification(data?.error || "Error al crear la orden", "error");
        }
      });
    },

    clearCartAfterSave() {
      // Limpiar el carrito
      this.cart = [];

      // Limpiar totales
      this.totals = { qty: 0, total: 0 };

      // Limpiar localStorage
      localStorage.removeItem("cart");

      // Limpiar formulario
      this.motive = null;
      this.validatemotive = false;

      // Limpiar datos de búsqueda de productos
      this.groupedData = [];
      this.hasSearched = false;
      this.filters = {
        field: "NOMBRE",
        value: "",
      };

      // Reset del formulario si existe
      if (this.$refs.motiveform) {
        this.$refs.motiveform.resetValidation();
      }

      console.log(
        "Cart y datos de búsqueda limpiados después de guardar orden exitosamente"
      );
    },

    handleNewOrder() {
      this.resetOrder();
      this.showOrderDocument = false;
    },

    handleViewList() {
      this.$router.push("/orders");
    },

    resetOrder() {
      this.cart = [];
      this.motive = null;
      this.totals = { qty: 0, total: 0 };
      this.groupedData = [];
      this.hasSearched = false;
      this.filters = { field: "NOMBRE", value: "" };
      this.orderDocument = null;
      this.updateTotal();
      localStorage.removeItem("cart");
    },

    // Helper Methods
    getStockClass(qty) {
      qty = parseInt(qty) || 0;
      if (qty === 0) return "error--text font-weight-bold";
      if (qty < 5) return "warning--text font-weight-bold";
      return "success--text";
    },

    getMarginClass(margin) {
      margin = parseFloat(margin) || 0;
      if (margin < 0.2) return "error--text";
      if (margin < 0.3) return "warning--text";
      return "success--text";
    },

    getRecommendation(item) {
      const qty = parseInt(item.qty) || 0;
      const sales = parseInt(item.sales_qty) || 0;
      const margin = parseFloat(item.margen) || 0;

      let suggestedQty = 0;

      // Sin stock y con ventas
      if (qty === 0 && sales > 0) {
        suggestedQty = Math.max(Math.ceil(sales * 1.5), 5);
        return {
          color: "error",
          icon: "mdi-alert",
          text: "Urgente",
          reason: "Sin stock pero con ventas recientes",
          suggestedQty,
        };
      }

      // Stock bajo con buenas ventas
      if (qty < sales * 0.5 && sales > 5) {
        suggestedQty = Math.max(Math.ceil(sales - qty), 3);
        return {
          color: "success",
          icon: "mdi-check",
          text: "Pedir",
          reason: "Stock bajo vs ventas",
          suggestedQty,
        };
      }

      // Buen margen y rotación
      if (margin > 0.3 && sales > 10) {
        suggestedQty = Math.max(Math.ceil(sales * 0.8), 2);
        return {
          color: "success",
          icon: "mdi-star",
          text: "Óptimo",
          reason: "Buen margen y rotación",
          suggestedQty,
        };
      }

      // Stock suficiente
      if (qty > sales * 2) {
        return {
          color: "grey",
          icon: "mdi-pause",
          text: "Esperar",
          reason: "Stock suficiente",
          suggestedQty: 0,
        };
      }

      // Revisar caso por caso
      suggestedQty = sales > 0 ? Math.max(Math.ceil(sales * 0.5), 1) : 1;
      return {
        color: "info",
        icon: "mdi-help",
        text: "Revisar",
        reason: "Evaluar según necesidad",
        suggestedQty,
      };
    },
    // Métodos auxiliares mejorados
    getStockRotation(item) {
      const stock = parseInt(item.qty) || 0;
      const sales = parseInt(item.sales_qty) || 0;
      return sales > 0 ? Math.min(sales / Math.max(stock, 1), 2) : 0;
    },

    getRotationColor(item) {
      const rotation = this.getStockRotation(item);
      if (rotation > 1.5) return "success";
      if (rotation > 0.8) return "warning";
      return "error";
    },

    applySuggestedQuantity(item) {
      const suggestion = this.getRecommendation(item);
      if (suggestion.suggestedQty > 0) {
        item.cantidad = suggestion.suggestedQty;
        this.showNotification(
          `Cantidad sugerida aplicada: ${suggestion.suggestedQty}`,
          "info"
        );
      }
    },

    selectAllRecommended() {
      let count = 0;
      this.groupedData.forEach((item) => {
        const rec = this.getRecommendation(item);
        if (rec.color === "success" && rec.suggestedQty > 0) {
          item.cantidad = rec.suggestedQty;
          count++;
        }
      });

      if (count > 0) {
        this.showNotification(`${count} productos recomendados seleccionados`, "success");
      }
    },

    clearAllQuantities() {
      this.groupedData.forEach((item) => {
        item.cantidad = 0;
      });
      this.showNotification("Cantidades limpiadas", "info");
    },

    // Supplier Management Methods
    search_suppliers(e) {
      if (e) e.preventDefault();

      this.has_searched_suppliers = true;
      this.loading_suppliers = true;

      // If no search term, show all suppliers
      if (!this.supplier_search || this.supplier_search.trim() === "") {
        if (this.all_suppliers && this.all_suppliers.length > 0) {
          this.suppliers_list = [...this.all_suppliers];
          this.loading_suppliers = false;
          this.showNotification(
            `${this.suppliers_list.length} proveedores cargados`,
            "success"
          );
          return;
        }
      }

      const filters = [
        {
          field: "store",
          operator: "=",
          value: window.store.store_id.substring(0, 6),
        },
      ];

      // Add search filter only if there's a search term
      if (this.supplier_search && this.supplier_search.trim() !== "") {
        filters.push({
          field: "nombre",
          operator: " LIKE ",
          value: this.supplier_search,
        });
      }

      const qry = {
        store: window.store.store_id,
        table: "proveedores",
        filters: filters,
      };

      webserver("get_dataTable", qry, (data) => {
        console.log("Suppliers data:", data);
        this.loading_suppliers = false;

        if (data && data.length > 0) {
          // Normalize the data structure to match what Order expects
          this.suppliers_list = data.map((itm) => ({
            ...itm,
            code: itm.codigo,
            PartyIdentification: itm.id_nit,
            PartyName: itm.nombre,
            AddressLine: itm.direccion,
            CityName: itm.Ciudad,
            PartyEmail: itm.email,
            PartyPhone: itm.telefono,
          }));
          this.showNotification(`${data.length} proveedores encontrados`, "success");
        } else {
          this.suppliers_list = [];
          this.showNotification("No se encontraron proveedores", "info");
        }
      });
    },

    select_supplier(supplier) {
      // Check if we're changing supplier and have existing data
      const hasExistingData = this.cart.length > 0 || this.groupedData.length > 0;
      const isDifferentSupplier =
        this.supplier && this.supplier.code !== (supplier.codigo || supplier.code);

      if (hasExistingData && isDifferentSupplier) {
        // Prepare confirmation dialog data
        this.pending_supplier = supplier;
        this.change_impact = {
          cart_items: this.cart.length,
          search_results: this.groupedData.length,
          has_motive: !!this.motive,
        };

        // Show elegant Vuetify confirmation dialog
        this.confirm_change_dialog = true;
        return; // Wait for user confirmation
      }

      // If no conflict, proceed directly
      this.applySupplierChange(supplier);
    },

    // Reset all product-related data when changing supplier
    resetProductData() {
      console.log("Resetting product data due to supplier change...");

      // Clear cart
      this.cart = [];

      // Clear search results
      this.groupedData = [];

      // Reset search filters
      this.filters = {
        field: "NOMBRE",
        value: "",
      };

      // Reset search state
      this.hasSearched = false;

      // Reset totals
      this.totals = { qty: 0, total: 0 };

      // Clear localStorage cart
      localStorage.removeItem("cart");

      // Reset form validation
      this.motive = null;
      this.validatemotive = false;
      if (this.$refs.motiveform) {
        this.$refs.motiveform.resetValidation();
      }

      console.log("Product data reset completed:", {
        cart: this.cart.length,
        groupedData: this.groupedData.length,
        totals: this.totals,
        hasSearched: this.hasSearched,
      });
    },

    // Apply supplier change (called after confirmation or directly)
    applySupplierChange(supplier) {
      // Store previous supplier code for logging
      const previousSupplier = this.supplier ? this.supplier.PartyName : "ninguno";
      const hadExistingData = this.cart.length > 0 || this.groupedData.length > 0;

      // Normalize supplier data structure
      this.supplier = {
        code: supplier.codigo || supplier.code,
        PartyName: supplier.nombre,
        PartyIdentification: supplier.id_nit,
        AddressLine: supplier.direccion,
        CityName: supplier.Ciudad,
        PartyEmail: supplier.email,
        PartyPhone: supplier.telefono,
        // Keep original data for compatibility
        nombre: supplier.nombre,
        id_nit: supplier.id_nit,
        direccion: supplier.direccion,
        ciudad: supplier.Ciudad,
        email: supplier.email,
        celular: supplier.telefono,
      };

      // Clear all product-related data if there was existing data
      if (hadExistingData) {
        this.resetProductData();
      }

      // Close dialogs
      this.supplier_dialog = false;
      this.confirm_change_dialog = false;

      // Show appropriate notification
      if (hadExistingData && previousSupplier !== "ninguno") {
        this.showNotification(
          `Proveedor cambiado de ${previousSupplier} a ${supplier.nombre}. Documento reiniciado.`,
          "warning"
        );
      } else {
        this.showNotification(`Proveedor ${supplier.nombre} seleccionado`, "success");
      }

      console.log("Supplier applied:", this.supplier);
      if (hadExistingData) {
        console.log("Product data reset completed");
      }
    },

    // Confirm supplier change from dialog
    confirmSupplierChange() {
      if (this.pending_supplier) {
        this.applySupplierChange(this.pending_supplier);
        this.pending_supplier = null;
        this.change_impact = {
          cart_items: 0,
          search_results: 0,
          has_motive: false,
        };
      }
    },

    // Cancel supplier change
    cancelSupplierChange() {
      this.confirm_change_dialog = false;
      this.pending_supplier = null;
      this.change_impact = {
        cart_items: 0,
        search_results: 0,
        has_motive: false,
      };
      this.showNotification("Cambio de proveedor cancelado", "info");
    },

    // Load suppliers for the dialog (similar to Orders component)
    loadSuppliersForDialog() {
      const qry = {
        store: window.store.store_id,
        table: "proveedores",
        filters: [
          {
            field: "store",
            operator: "=",
            value: window.store.store_id.substring(0, 6),
          },
        ],
      };

      webserver("get_dataTable", qry, (data) => {
        if (data && data.length > 0) {
          // Store all suppliers for quick access
          this.all_suppliers = data.map((itm) => ({
            ...itm,
            code: itm.codigo,
            PartyIdentification: itm.id_nit,
            PartyName: itm.nombre,
            AddressLine: itm.direccion,
            CityName: itm.Ciudad,
            PartyEmail: itm.email,
            PartyPhone: itm.telefono,
          }));
          console.log(`${data.length} proveedores cargados para diálogo`);
        }
      });
    },
  },
};
</script>

<style scoped>
/* Estilos del layout de Receipt_in */
.status-chip {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 5;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.75rem;
  color: #666;
  margin-top: 2px;
}

/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.dialog-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

/* Order Header - Equal Heights */
.equal-height-row {
  min-height: 200px;
}

.equal-height-row .v-col {
  display: flex;
}

.equal-height-row .v-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.store-content,
.supplier-content {
  min-height: 140px;
  display: flex;
  flex-direction: column;
}

.order-header {
  background-color: #fafafa;
}

.store-info {
  display: flex;
  align-items: center;
}

.store-name {
  font-weight: 600;
  font-size: 1.125rem;
  color: #333;
}

.store-location {
  color: #666;
  font-size: 0.875rem;
}

/* Table Styles */
.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 12px 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
}

.minimal-table >>> tbody tr:hover {
  background-color: #fafafa !important;
}

.minimal-table >>> tbody tr td {
  padding: 12px 16px !important;
  font-size: 0.875rem !important;
}

/* Product Info */
.product-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.product-name {
  font-weight: 500;
  color: #333;
}

.product-details {
  font-size: 0.75rem;
  color: #666;
  display: flex;
  align-items: center;
}

.product-search-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Quantity Controls */
.quantity-control {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
}

.quantity-input {
  width: 60px;
}

.quantity-input >>> input {
  text-align: center;
}

.quantity-search-input {
  width: 80px;
}

.quantity-search-input >>> input {
  text-align: center;
}

/* Stock Info */
.stock-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

/* Search Stats */
.search-stat {
  text-align: center;
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

/* Empty State */
.empty-state {
  min-height: 300px;
}

/* Responsive */
@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
  }
}

/* Mejoradas búsquedas */
.quantity-container {
  width: 80px;
}

.quantity-search-input >>> input {
  text-align: center;
  font-weight: 500;
}

.stock-info {
  min-width: 100px;
}

/* Indicadores de rotación */
.v-progress-linear {
  border-radius: 2px;
}

/* Chips mejorados */
.v-chip.v-size--x-small {
  font-size: 0.6rem;
  height: 18px;
}

/* Supplier info styles */
.supplier-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Supplier change dialog styles */
.change-summary {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
}

.change-summary .text-decoration-line-through {
  opacity: 0.7;
}

/* Enhanced dialog styling */
.v-dialog .v-card .v-card__title.warning {
  background: linear-gradient(45deg, #ff9800, #f57c00) !important;
}

.v-alert.v-alert--outlined.v-alert--type-warning {
  border-left: 4px solid #ff9800 !important;
}

/* Responsive */
@media (max-width: 960px) {
  .product-search-info {
    max-width: 180px;
  }

  .quantity-container {
    width: 70px;
  }
}
</style>
